/**
 * Vertical pane stacking — relative heights to normalised bands.
 *
 * Pure: no SciChart import, so the arithmetic behind pane layout is unit-tested
 * without a browser or a WASM context, per this package's layering.
 *
 * Both the renderer and the divider overlay derive their geometry from here.
 * Computing the boundaries twice from the same weights is exactly how a divider
 * ends up a few pixels off the seam it is meant to sit on.
 */

/** One pane's band in normalised (0..1) parent coordinates. */
export interface PaneBand {
  y: number;
  height: number;
}

/**
 * Relative weight of a `Pane.height`.
 *
 * Only `fr` units carry a weight. A pixel height (`"300px"`) has no meaning in a
 * normalised stack, so it falls back to 1 rather than being silently mixed with
 * fractions — the contract permits both spellings, and quietly treating 300px as
 * 300fr would swallow the pane's neighbours.
 */
export function paneWeight(height?: string): number {
  if (height && height.endsWith("fr")) {
    const n = parseFloat(height);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 1;
}

/** Relative weights of a pane list, in display order. */
export function paneWeights(panes: readonly { height?: string }[]): number[] {
  return panes.map((p) => paneWeight(p.height));
}

/**
 * Stack weights into contiguous normalised bands, summing to exactly 1.
 *
 * The last band is snapped to the bottom rather than accumulated to it. Heights
 * round-trip through `fr` strings with three decimals, so the fractions drift by
 * a fraction of a percent — enough to leave a visible strip of background under
 * the bottom pane. Snapping puts the drift inside the last band, where it is
 * invisible, instead of below it.
 */
export function stackFractions(weights: readonly number[]): PaneBand[] {
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  const out: PaneBand[] = [];
  let y = 0;
  weights.forEach((w, i) => {
    const last = i === weights.length - 1;
    const height = last ? 1 - y : w / total;
    out.push({ y, height });
    y += height;
  });
  return out;
}

/**
 * The normalised y of each interior boundary — one per divider.
 *
 * `n` panes have `n - 1` seams; the outer edges are the container, not dividers.
 */
export function paneBoundaries(weights: readonly number[]): number[] {
  const bands = stackFractions(weights);
  return bands.slice(0, -1).map((b) => b.y + b.height);
}

/**
 * Smallest share a pane may be dragged to, so it can never vanish.
 *
 * The fallback, for a caller with no container to measure. Prefer
 * {@link MIN_PANE_PX}, which is what the divider overlay passes: a *share* means
 * a different number of pixels on every screen, so 6% is 48px on an 800px chart
 * and 19px on a phone — the floor is loosest exactly where a pane is already
 * too small to read.
 */
export const MIN_PANE_FRACTION = 0.06;

/**
 * Smallest height a pane may be dragged to, in CSS px.
 *
 * Set by the owner, 2026-09-19, replacing the 6% share above as the floor that
 * actually applies: a signal strip or a volume pane is legitimately tiny, and
 * the only real requirement is that a pane can never be dragged to nothing and
 * become unrecoverable.
 *
 * A pixel floor needs the container's height, which a pure function does not
 * have — so it is the *caller* that converts, and `PaneDividers` already
 * measures the host for the drag itself.
 */
export const MIN_PANE_PX = 25;

/**
 * Move one boundary, returning new `fr` heights.
 *
 * Only the two panes either side of the seam change; their combined share is
 * preserved, so dragging one boundary never disturbs the rest of the stack —
 * the same rule `SectionGrid` uses for layout dividers, and the reason a drag
 * feels local rather than reflowing everything.
 *
 * @param weights  - current relative weights, in display order
 * @param boundary - index of the pane above the seam
 * @param fraction - pointer position as a 0..1 share of the container
 * @param minShare - smallest share either side may be left at. A caller that
 *   knows the container's height passes `MIN_PANE_PX / height`; the default is
 *   {@link MIN_PANE_FRACTION}, for one that does not.
 */
export function resizeAtBoundary(
  weights: readonly number[],
  boundary: number,
  fraction: number,
  minShare: number = MIN_PANE_FRACTION,
): string[] {
  const next = [...weights];
  const above = next[boundary];
  const below = next[boundary + 1];
  if (above === undefined || below === undefined) return next.map(toFr);

  const total = next.reduce((a, b) => a + b, 0) || 1;
  const before = next.slice(0, boundary).reduce((a, b) => a + b, 0);
  const pair = above + below;

  // Convert the pointer's container-relative position into a weight for the
  // upper pane, then clamp so neither side collapses.
  const wanted = fraction * total - before;

  // **The floor is capped at half the pair, because a pixel one can be
  // impossible.** A
  // share is always satisfiable; 25px is not, on a chart shorter than 50px or a
  // seam between two panes that are already thin. Without the cap the clamp
  // inverts — `pair - min` falls below `min`, the lower pane takes a *negative*
  // weight, and `stackFractions` hands the renderer a band running backwards.
  const min = Math.min(minShare * total, pair / 2);
  const upper = Math.max(min, Math.min(wanted, pair - min));

  next[boundary] = upper;
  next[boundary + 1] = pair - upper;
  return next.map(toFr);
}

const toFr = (w: number): string => `${w.toFixed(3)}fr`;
